import React, { useMemo, useState } from "react";
import "./appHeader.scss";
import { Button, Radio, Tooltip } from "antd";
import { LogoutOutlined, ReloadOutlined } from "@ant-design/icons";
import NotificationBell from "../NotificationBell";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import AuthService from "../../../services/AuthService/auth.service";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import ApiRoutes from "../../../routes/routeConstants/apiRoutes";
import { useHistory } from "react-router-dom";
import { AppRoutes } from "../../../routes/routeConstants/appRoutes";
import ProfileService from "../../../services/Profile/profile.service";
import Notification from "../Notification";
import { NotificationTypes } from "../../../enums/notificationTypes";
import { Admin } from "../../../models/Admin/admin.model";
import { Doctor } from "../../../models/Doctor/doctor.model";
import { ScanCenter } from "../../../models/ScanCenter/scanCenter.model";
import {
  getAppShellBaseUrl,
  NEW_APP_URL,
  redirectToAppShell,
} from "../../utils/appShellRedirect";
import { buildHandoffUrl } from "../../utils/handoffClient";

interface AppHeaderProps extends AuthReducerProps {
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
}

function AppHeader({ authenticated, userRole, user, collapsed, setCollapsed }: AppHeaderProps) {
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [appSwitchLoading, setAppSwitchLoading] = useState(false);

  const handleLogout = () => {
    setLoading(true);
    AuthService.logout(
      userRole === UserRoleEnum.ADMIN
        ? ApiRoutes.ADMIN_SIGN_OUT
        : userRole === UserRoleEnum.SCAN_CENTER
        ? ApiRoutes.SCAN_CENTER_SIGN_OUT
        : ApiRoutes.DOCTOR_SIGN_OUT,
      () => {
        history.push(AppRoutes.LOGIN);
      },
      () => {},
      () => {
        setLoading(false);
      }
    );
  };

  // Source of truth for which shell we're on is the live window origin, NOT
  // the user's stored `isNewApp` preference. Per product decision: landing
  // on `app.radiolinq.com` → Classic, landing on `appv2.radiolinq.com` →
  // New. The toggle is a session-time jump, not a sticky redirect.
  const isOnNewShell = useMemo(() => {
    if (!NEW_APP_URL) return false;
    try {
      return new URL(NEW_APP_URL).origin === window.location.origin;
    } catch (_) {
      return false;
    }
  }, []);

  // Render the toggle only when the *other* shell's base URL is configured.
  // Keeps it hidden on `localhost` / unconfigured envs where flipping would
  // 404 the user.
  const otherShellBase = useMemo(
    () => getAppShellBaseUrl(!isOnNewShell),
    [isOnNewShell]
  );
  const showAppSwitch = Boolean(otherShellBase);

  // Toggle flow:
  //   1. Fire-and-forget the `isNewApp` profile update (write-only analytics
  //      — backend can answer "% of users on new"). Failures are non-fatal;
  //      we don't want the user waiting on a round-trip just to switch UIs.
  //   2. Build a cross-origin handoff URL that carries `Authorization` +
  //      devise `auth` headers + cached `user` JSON in the URL fragment.
  //      The receiver (`HandoffPage`) writes them into its own localStorage
  //      and reloads, so the user never sees the login screen.
  //   3. If no auth is sitting in localStorage (anonymous tab, edge case),
  //      we fall back to a bare redirect — they'll land on /auth/login on
  //      the other shell, which is the only sensible outcome.
  const handleAppSwitchToggle = (nextValue: boolean) => {
    if (appSwitchLoading) return;
    if (nextValue === isOnNewShell) return;

    const targetBase = getAppShellBaseUrl(nextValue);
    if (!targetBase) {
      Notification({
        message: nextValue
          ? "New app URL is not configured."
          : "Old app URL is not configured.",
        type: NotificationTypes.ERROR,
      });
      return;
    }

    setAppSwitchLoading(true);

    if (userRole && user?.id) {
      let updatedUserInstance: Admin | Doctor | ScanCenter;
      if (userRole === UserRoleEnum.ADMIN) {
        updatedUserInstance = Object.assign(new Admin(), user, { isNewApp: nextValue });
      } else if (userRole === UserRoleEnum.DOCTOR) {
        updatedUserInstance = Object.assign(new Doctor(), user, { isNewApp: nextValue });
      } else {
        updatedUserInstance = Object.assign(new ScanCenter(), user, { isNewApp: nextValue });
      }
      ProfileService.updateIsNewApp(
        userRole,
        updatedUserInstance,
        () => {},
        () => {},
        () => {}
      );
    }

    const returnPath =
      (window.location.pathname || "/") + (window.location.search || "");
    const handoffUrl = buildHandoffUrl(targetBase, returnPath);
    if (handoffUrl) {
      window.location.href = handoffUrl;
    } else {
      redirectToAppShell(targetBase);
    }
  };

  return (
    authenticated && (
        <div className={`app-header ${collapsed ? 'collapsed' : 'expanded'}`}>
            <div className="app-header__user-info">
                <div className="app-header__user-name">
                    {user?.name ? user?.name : `${user?.firstName} ${user?.lastName}`}
                </div>
            </div>
            <div className="button-group">
              {/* {showAppSwitch && (
                <Tooltip
                  title={
                    isOnNewShell
                      ? "Switch back to Classic"
                      : "Try the new app (Beta)"
                  }
                  placement="bottom"
                >
                  <Radio.Group
                    size="small"
                    className="app-header__app-switch"
                    value={isOnNewShell ? "new" : "classic"}
                    onChange={(e) =>
                      handleAppSwitchToggle(e.target.value === "new")
                    }
                    disabled={appSwitchLoading}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="classic">Classic</Radio.Button>
                    <Radio.Button value="new">New (Beta)</Radio.Button>
                  </Radio.Group>
                </Tooltip>
              )} */}
              <NotificationBell />
              <Button type="primary" onClick={() => window.location.reload()}>
                <ReloadOutlined/>
                <span className="responsive-span">
                  Hard Refresh
                </span>
              </Button>
              <Button type="primary" loading={loading} onClick={handleLogout}>
                <LogoutOutlined/>
                <span className="responsive-span">
                  Logout
                </span>
              </Button>
            </div>
        </div>
    )
  );
}

export default AuthContainer(AppHeader);
